import { useAtom, useAtomValue } from "jotai";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useQueryParam } from "@/hooks/useQueryParam";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getCustomer } from "@/services/api";
import {
  accessTokenAtom,
  destinationAtom,
  isAnonymousUserAtom,
  refreshTokenAtom,
  showsSessionExpiredModalAtom,
  verifierAtom,
} from "@/utils/atoms";
import { agentLoginStatus, fetchAuthToken } from "@/utils/authentication";
import { hasCompleteMandatoryPersonalData } from "@/utils/customerUtils";
import { log } from "@/utils/log";

/*
 * This hook handles the redirect to the portal from the Oauth2 provider after the user has logged in.
 * We decide where to redirect the user based on the user's onboarding status.
 */
export const useLoginRedirect = (): boolean => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [accessToken, setAccessToken] = useAtom(accessTokenAtom);
  const [refreshToken, setRefreshToken] = useAtom(refreshTokenAtom);
  const setShowSessionExpiredModal = useAtom(showsSessionExpiredModalAtom)[1];
  const destination = useAtomValue(destinationAtom);
  const verifier = useAtomValue(verifierAtom);
  const { dataContext } = useContext(DataContext);
  const isEmailVerified = dataContext?.isEmailVerified;

  const navigate = useNavigate();
  const [code] = useQueryParam("code");

  // Check if we're in the partner app
  const authorized = !!accessToken;

  // Check if we're attempting to login as agent
  const isAgentLogin = refreshToken === agentLoginStatus;
  // Agent login is only "active" if tokens are valid
  const isAgentAuthenticated = isAgentLogin && !!accessToken;

  // It fetches our auth tokens.
  useEffect(() => {
    if (code && verifier && !authorized) {
      setIsRedirecting(true);
      fetchAuthToken(code, verifier)
        .then(({ access_token, refresh_token }) => {
          setAccessToken(access_token);
          setRefreshToken(refresh_token);
          // Clear session expired modal state on successful OAuth redirect login
          setShowSessionExpiredModal(undefined);
        })
        .catch((e) => {
          log(e);
        })
        .finally(() => {
          setIsRedirecting(false);
        });
    }
  }, [
    authorized,
    code,
    setAccessToken,
    setRefreshToken,
    setShowSessionExpiredModal,
    verifier,
  ]);

  const [isAnonymousUser] = useAtom(isAnonymousUserAtom);

  useEffect(() => {
    if (isEmailVerified === undefined) {
      return;
    }

    // Skip redirect logic entirely if we’re logged in as an agent
    if (isAgentAuthenticated) {
      return;
    }

    if (accessToken && refreshToken && !isAnonymousUser && isEmailVerified) {
      // Only set isRedirecting when actually performing a redirect
      setIsRedirecting(true);

      // For customer app, check customer data and onboarding status
      getCustomer()
        .then((customerData) => {
          if (hasCompleteMandatoryPersonalData(customerData)) {
            navigate(destination || portalRoutes.cockpit.base, {
              replace: true,
            });
          } else {
            navigate(portalRoutes.personalDataOnboarding, { replace: true });
          }
        })
        .catch(() => {
          setAccessToken(null);
          setRefreshToken(null);
        })
        .finally(() => {
          setIsRedirecting(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    destination,
    isAnonymousUser,
    navigate,
    refreshToken,
    setAccessToken,
    setRefreshToken,
    isEmailVerified,
    isAgentLogin,
  ]);

  return isRedirecting;
};
